import React from 'react';

import {
  ActionIcon,
  AutocompleteItem,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import AppointmentCoachRoleAutoComplete, {
  AppointmentCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentCoachRoleAutoComplete';
import AppointmentTypeAutoComplete, {
  AppointmentTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentTypeAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs, { Dayjs } from 'dayjs';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult } from 'react-query';
import { Appointment, AppointmentRetrieve, AppointmentWrite, ErrorMessage } from 'types';
import { TimeUnitEnum } from 'types/enums';
import { getChangedKeysForAppointmentEdit } from 'utils/analytics-selectors';
import { isDateTimePast } from 'utils/date-functions';
import { calculateEventEndDateTime, getDayDiff } from 'utils/event.helpers';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  appointmentId: string;
  startDate: Date;
  startTime: string;
  duration: number;
  endDate: Date | null;
  appointmentType: AppointmentTypeAutoCompleteValue;
  appointmentCoachAppointmentCoachRoles: {
    appointmentCoachAppointmentCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    appointmentCoachRole: AppointmentCoachRoleAutoCompleteValue;
  }[];
  location: LocationAutoCompleteValue;
  rootAppointment: string | null;

  cancellationSettings: {
    useLateCancellationFee: boolean;
    useNoShowFee: boolean;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
  };
  appointmentEnd: Dayjs | null;
  dayDiff: string;
  rrule: string | null;
}

interface IDetailsEditSelectedDayFormProps {
  appointment: AppointmentRetrieve;
  closeHandler: () => void;
  formId: string;
  updateMutation: UseMutationResult<AxiosResponse<Appointment>, AxiosError<ErrorMessage>, AppointmentWrite>;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const DetailsEditSelectedDayForm = ({
  appointment,
  closeHandler,
  formId,
  updateMutation,
}: IDetailsEditSelectedDayFormProps): JSX.Element => {
  const theme = useMantineTheme();
  const { country } = useLocalization();
  const staffTracking = useStaffTracking();

  const isPastAppointment = isDateTimePast(appointment.startDate, appointment.startTime);

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();
  const cancellationSettings = appointment.cancellationSettings;

  const initialappointmentEnd = calculateEventEndDateTime(
    appointment.duration,
    appointment.startTime,
    dayjs(appointment.startDate).toDate(),
  );

  const formValues: FormValues = {
    ...appointment,
    startDate: dayjs(appointment.startDate).toDate(),
    endDate: appointment.rootAppointment?.endDate
      ? dayjs(appointment.rootAppointment.endDate).toDate()
      : null,
    startTime: dayjs(`${appointment.startDate} ${appointment.startTime}`).format('HH:mm').toString(),
    appointmentCoachAppointmentCoachRoles: appointment.appointmentCoachAppointmentCoachRoles.map(acacr => ({
      appointmentCoachAppointmentCoachRoleId: acacr.appointmentCoachAppointmentCoachRoleId,
      coach: {
        staffId: acacr.coach.staffId,
        value: acacr.coach.staffId,
        label: acacr.coach.fullName,
      },
      appointmentCoachRole: {
        appointmentCoachRoleId: acacr.appointmentCoachRole.appointmentCoachRoleId,
        value: acacr.appointmentCoachRole.appointmentCoachRoleId,
        label: acacr.appointmentCoachRole.name,
      },
    })),
    appointmentType: {
      appointmentTypeId: appointment.appointmentType?.appointmentTypeId || '',
      value: appointment.appointmentType?.appointmentTypeId || '',
      label: appointment.appointmentType?.name || '',
    },
    location: {
      locationId: appointment.location?.locationId || '',
      label: appointment.location?.name || '',
      value: appointment.location?.locationId || '',
    },
    rootAppointment: appointment.rootAppointment?.appointmentId || null,
    cancellationSettings: {
      useLateCancellationFee: cancellationSettings.cancellationFee !== 0,
      useNoShowFee: cancellationSettings.noShowFee !== 0,
      allowLateCancellation: cancellationSettings.allowLateCancellation ?? true,
      cancellationInterval: cancellationSettings.cancellationInterval ?? 'minute',
      cancellationIntervalCount: cancellationSettings.cancellationIntervalCount ?? 0,
      cancellationFee: cancellationSettings.cancellationFee ?? 0,
      cancellationCreditDeduction: cancellationSettings.cancellationCreditDeduction ?? false,
      noShowFee: cancellationSettings.noShowFee ?? 0,
      noShowCreditDeduction: cancellationSettings.noShowCreditDeduction ?? false,
    },
    appointmentEnd: initialappointmentEnd,
    dayDiff: getDayDiff(dayjs(appointment.startDate), initialappointmentEnd),
  };

  const initialValues = formValues;

  const form = useForm<FormValues>({
    initialValues,

    validate: values => ({
      appointmentType: values.appointmentType ? null : 'Appointment type is required',
      duration: values.duration < 15 ? 'Duration must be more or equal 15 mins' : null,
      startDate: values.startDate ? null : 'Start date is required',
      startTime: values.startTime ? null : 'Start time is required',
    }),
  });

  const handleCloseAndFormReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleAddCoach = (): void => {
    form.insertListItem('appointmentCoachAppointmentCoachRoles', {
      appointmentCoachAppointmentCoachRoleId: uuidv4(),
      coach: {
        staffId: '',
        value: '',
        label: '',
      },
      appointmentCoachRole: {
        appointmentCoachRoleId: '',
        value: '',
        label: '',
      },
    });
  };

  const handleCoachChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedCoach = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`appointmentCoachAppointmentCoachRoles.${index}.coach`, selectedCoach);
  };

  const handleAppointmentCoachRoleChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedAppointmentCoachRole = {
      appointmentCoachRoleId: item ? item.appointmentCoachRoleId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(
      `appointmentCoachAppointmentCoachRoles.${index}.appointmentCoachRole`,
      selectedAppointmentCoachRole,
    );
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleAppointmentTypeChange = (item: AppointmentTypeAutoCompleteValue | null): void => {
    const selectedAppointmentType = {
      appointmentTypeId: item ? item.appointmentTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('appointmentType', selectedAppointmentType);
  };

  const handleUpdate = (values: FormValues): void => {
    if (updateMutation.isLoading) return;
    const { errorNotification, successNotification } = notificationHandler();

    const payload = {
      ...values,
      appointmentType: values.appointmentType.appointmentTypeId,
      appointmentCoachAppointmentCoachRoles: values.appointmentCoachAppointmentCoachRoles.map(acr => ({
        appointmentCoachRole: acr.appointmentCoachRole.appointmentCoachRoleId,
        coach: acr.coach.staffId,
      })),
      location: values.location?.locationId || null,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
      endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
      cancellationSettings: {
        allowLateCancellation: values.cancellationSettings.allowLateCancellation,
        cancellationInterval: values.cancellationSettings.cancellationInterval,
        cancellationIntervalCount: values.cancellationSettings.cancellationIntervalCount,
        cancellationFee:
          values.cancellationSettings.useLateCancellationFee &&
          values.cancellationSettings.allowLateCancellation
            ? values.cancellationSettings.cancellationFee
            : 0,
        cancellationCreditDeduction: values.cancellationSettings.allowLateCancellation
          ? values.cancellationSettings.cancellationCreditDeduction
          : false,
        noShowFee: values.cancellationSettings.useNoShowFee ? values.cancellationSettings.noShowFee : 0,
        noShowCreditDeduction: values.cancellationSettings.noShowCreditDeduction,
      },
      athletes: [],
      editThisEventOnly: false,
      sendNotification: true,
    };

    const changedKeys: string[] = getChangedKeysForAppointmentEdit(initialValues, values);

    updateMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(`${values.appointmentType.label} edited successfully`);
        if (changedKeys.length > 0) {
          staffTracking.trackEventUpdate(
            appointment.appointmentId,
            values.appointmentType.appointmentTypeId,
            values.appointmentType.label,
            'appointment',
            'this day only',
            changedKeys,
          );
        }
        handleCloseAndFormReset();
      },
      onError: error => {
        errorNotification('Updating appointment failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    if (form.values.duration && form.values.startTime) {
      const appointmentEnd = calculateEventEndDateTime(
        form.values.duration,
        form.values.startTime,
        dayjs(form.values.startDate).toDate(),
      );
      form.setFieldValue('appointmentEnd', appointmentEnd);
      form.setFieldValue('dayDiff', getDayDiff(dayjs(form.values.startDate), appointmentEnd));
    } else {
      form.setFieldValue('appointmentEnd', null);
      form.setFieldValue('dayDiff', '');
    }
  }, [form.values.duration, form.values.startTime, form.values.startDate]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleUpdate)}>
      <Stack spacing='xs'>
        <Group grow align='top'>
          <AppointmentTypeAutoComplete
            data-cy='appointInput'
            label='Appointment Type'
            placeholder='Select a appointment type'
            value={form.values.appointmentType}
            error={form.errors.appointmentType}
            onChange={handleAppointmentTypeChange}
            required
            disabled
          />
          <LocationAutoComplete
            data-cy='locationAppointInput'
            label='Location'
            placeholder='Select a location'
            value={form.values.location}
            onChange={handleLocationChange}
            error={form.errors.location}
          />
        </Group>

        <Paper withBorder p='sm' bg='gray.0'>
          <Stack spacing='sm'>
            {!form.values.appointmentCoachAppointmentCoachRoles.length && (
              <Text size='sm' color='dimmed'>
                No Coach
              </Text>
            )}
            {form.values.appointmentCoachAppointmentCoachRoles.map((acacr, i) => (
              <Group
                key={acacr.appointmentCoachAppointmentCoachRoleId}
                position='apart'
                align='flex-start'
                noWrap
              >
                <StaffAutoComplete
                  {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.coach`)}
                  label='Coach'
                  placeholder='Select a coach'
                  onChange={value => handleCoachChange(value, i)}
                  required
                  disinclude={form.values.appointmentCoachAppointmentCoachRoles.map(a => a.coach.staffId)}
                  isCoach
                />
                <AppointmentCoachRoleAutoComplete
                  {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.appointmentCoachRole`)}
                  label='Role'
                  placeholder='Select a role'
                  onChange={value => handleAppointmentCoachRoleChange(value, i)}
                  required
                />
                <ActionIcon
                  mt='xl'
                  size='lg'
                  color='red'
                  onClick={() => form.removeListItem('appointmentCoachAppointmentCoachRoles', i)}
                >
                  <IconTrash size={theme.fontSizes.lg} />
                </ActionIcon>
              </Group>
            ))}
            <ActionIcon variant='transparent' color='kilo-blue' onClick={handleAddCoach}>
              <IconPlus />
            </ActionIcon>
          </Stack>
        </Paper>

        <Divider mt='xs' mx='-xl' />

        <Group grow align='top'>
          <NumberInput
            data-cy='durationAppointInput'
            {...form.getInputProps('duration')}
            label='Duration in minutes'
            placeholder='Enter duration'
            required
            min={15}
            max={60 * 24 * 7}
            disabled={isPastAppointment}
          />
          <div />
        </Group>

        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          <DateInput
            {...form.getInputProps('startDate')}
            label='Start Date'
            locale='us'
            required
            disabled={isPastAppointment}
            minDate={new Date()}
          />
          <Group spacing='xs' grow>
            <TimeInput
              data-cy='timeFromAppointInput'
              label='From'
              {...form.getInputProps('startTime')}
              required
              disabled={isPastAppointment}
            />
            <TimeInput
              label={
                <Group position='apart'>
                  <div>To</div>
                  <Text color='red'>{form.values.dayDiff}</Text>
                </Group>
              }
              value={form.values.appointmentEnd ? form.values.appointmentEnd.format('HH:mm') : ''}
              disabled
            />
          </Group>
        </SimpleGrid>

        <Divider mt='lg' mb={0} mx='-xl' />

        <ExpandableSection
          title='Additional Settings'
          opened={settingsOpened}
          handleToggle={handleSettingsToggle}
        >
          <Stack spacing='sm' px='md'>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Allow late cancellations'
                  checked={form.values?.cancellationSettings?.allowLateCancellation || false}
                  {...form.getInputProps('cancellationSettings.allowLateCancellation')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper
              label={
                form.values.cancellationSettings.allowLateCancellation
                  ? 'Time before the start of an appointment when a cancellation is considered late'
                  : 'Time before the start of an appointment when cancellations are no longer allowed'
              }
            >
              <SimpleGrid cols={2}>
                <NumberInput
                  min={0}
                  max={setMaxLateCancellationIntervalCount(
                    form.values.cancellationSettings.cancellationInterval,
                  )}
                  {...form.getInputProps('cancellationSettings.cancellationIntervalCount')}
                  required
                />
                <Select
                  data={timeUnitOptions}
                  {...form.getInputProps('cancellationSettings.cancellationInterval')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            {form.values?.cancellationSettings?.allowLateCancellation && (
              <>
                <Group noWrap>
                  <Switch
                    my='xs'
                    checked={form.values?.cancellationSettings?.useLateCancellationFee || false}
                    {...form.getInputProps('cancellationSettings.useLateCancellationFee')}
                  />
                  <Text size='sm'>Auto-Charge Fee for Late Cancellations</Text>
                  {form.values?.cancellationSettings?.useLateCancellationFee && (
                    <MoneyField
                      country={country}
                      min={0}
                      {...form.getInputProps('cancellationSettings.cancellationFee')}
                    />
                  )}
                </Group>
                <Input.Wrapper>
                  <SimpleGrid cols={1}>
                    <Switch
                      my='xs'
                      label='Deduction of credit for late cancellations'
                      checked={form.values?.cancellationSettings?.cancellationCreditDeduction || false}
                      {...form.getInputProps('cancellationSettings.cancellationCreditDeduction')}
                    />
                  </SimpleGrid>
                </Input.Wrapper>
              </>
            )}
            <Divider />
            <Group noWrap>
              <Switch
                my='xs'
                checked={form.values?.cancellationSettings?.useNoShowFee || false}
                {...form.getInputProps('cancellationSettings.useNoShowFee')}
              />
              <Text size='sm'>Auto-Charge Fee for No Shows</Text>
              <MoneyField
                country={country}
                min={0}
                {...form.getInputProps('cancellationSettings.noShowFee')}
                disabled={!form.values?.cancellationSettings?.useNoShowFee}
              />
            </Group>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Deduction of credit for No Shows'
                  checked={form.values?.cancellationSettings?.noShowCreditDeduction || false}
                  {...form.getInputProps('cancellationSettings.noShowCreditDeduction')}
                />
              </SimpleGrid>
            </Input.Wrapper>
          </Stack>
        </ExpandableSection>
      </Stack>
    </form>
  );
};

export default DetailsEditSelectedDayForm;
